import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions } from './firebase'
import type { WorkspaceAction } from '../utils/workspacePermissions'
import { registerEphemeralTour } from '../components/tourguide/TourRegistry'
import { logTourSanitizerAnomaly, sanitizeTourDefinitionForRuntime } from '../components/tourguide/TourSanitizer'
import type { TourDefinition } from '../components/tourguide/types'

export interface GenerateTourOptions {
  prompt: string
  workspaceId: string
  canPerform: (action: WorkspaceAction) => boolean
}

export interface GenerateTourResult {
  tour: TourDefinition
  anchorsUsed: string[]
  rawText: string
}

type GenerateAITourResponse = {
  tour: unknown
  anchorsUsed?: string[]
  rawText?: string
}

export const isTourGenerationAvailable = (): boolean => true

export const generateAndRegisterTour = async (options: GenerateTourOptions): Promise<GenerateTourResult> => {
  const call = httpsCallable<{ workspaceId: string; prompt: string }, GenerateAITourResponse>(firebaseFunctions, 'generateAITour')
  const response = await call({
    workspaceId: options.workspaceId,
    prompt: options.prompt.trim(),
  })

  let tour: TourDefinition
  try {
    tour = sanitizeTourDefinitionForRuntime(response.data.tour, { canPerform: options.canPerform })
  } catch (error) {
    await logTourSanitizerAnomaly({
      workspaceId: options.workspaceId,
      tourId: response.data.tour && typeof response.data.tour === 'object' && 'id' in response.data.tour ? String((response.data.tour as { id?: unknown }).id ?? '') : undefined,
      reason: error instanceof Error ? error.message : 'Client-side tour sanitizer rejected the generated tour.',
      source: 'AI_TOUR_CLIENT_SANITIZER',
      anchorsUsed: response.data.anchorsUsed,
    }).catch(() => undefined)
    throw error
  }

  registerEphemeralTour(tour)
  return {
    tour,
    anchorsUsed: Array.isArray(response.data.anchorsUsed) ? response.data.anchorsUsed : [],
    rawText: typeof response.data.rawText === 'string' ? response.data.rawText : '',
  }
}
